"use client"

import * as React from "react"
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from "firebase/auth"

import { getFirebaseAuth, googleProvider, isAllowedEmail, ALLOWED_DOMAINS } from "./firebase"
import { setTokenProvider } from "./api"

type AuthState = {
  user: User | null
  /** true until Firebase has resolved the persisted session */
  loading: boolean
  /** signed in, but on a domain Workflows does not admit */
  rejected: string | null
  error: string | null
  signIn: () => Promise<void>
  /**
   * Email + password. Unlike the Google popup this does NOT require the host
   * to be an authorized Firebase domain, so it works over the raw VM IP where
   * the popup would fail with auth/unauthorized-domain.
   */
  signInWithPassword: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Fresh ID token for calling the API. Null when signed out. */
  getToken: () => Promise<string | null>
}

const Ctx = React.createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [rejected, setRejected] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  // Hand the API client a way to fetch a fresh token. getIdToken() refreshes
  // automatically when the current one is close to expiry.
  React.useEffect(() => {
    setTokenProvider(async () => {
      const u = getFirebaseAuth().currentUser
      return u ? await u.getIdToken() : null
    })
  }, [])

  React.useEffect(() => {
    const auth = getFirebaseAuth()
    return onAuthStateChanged(auth, (u) => {
      if (u && !isAllowedEmail(u.email)) {
        // Signed in with Google, but not on an allowed domain. Drop the
        // session immediately rather than leaving a half-authenticated user.
        setRejected(u.email ?? "unknown account")
        setUser(null)
        void fbSignOut(auth)
      } else {
        setRejected(null)
        setUser(u)
      }
      setLoading(false)
    })
  }, [])

  const signIn = React.useCallback(async () => {
    setError(null)
    setRejected(null)
    try {
      const auth = getFirebaseAuth()
      const cred = await signInWithPopup(auth, googleProvider())
      if (!isAllowedEmail(cred.user.email)) {
        setRejected(cred.user.email ?? "unknown account")
        await fbSignOut(auth)
      }
    } catch (e) {
      const code = (e as { code?: string })?.code ?? ""
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") return
      if (code === "auth/unauthorized-domain") {
        setError(
          `This host is not an authorized Firebase domain. Open Workflows over an SSH tunnel at http://localhost:3000 — "localhost" is authorized, a bare IP cannot be.`,
        )
        return
      }
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  const signInWithPasswordFn = React.useCallback(async (email: string, password: string) => {
    setError(null)
    setRejected(null)
    try {
      const auth = getFirebaseAuth()
      const cred = await signInWithEmailAndPassword(auth, email.trim(), password)
      if (!isAllowedEmail(cred.user.email)) {
        setRejected(cred.user.email ?? "unknown account")
        await fbSignOut(auth)
      }
    } catch (e) {
      const code = (e as { code?: string })?.code ?? ""
      const map: Record<string, string> = {
        "auth/invalid-credential": "Wrong email or password.",
        "auth/wrong-password": "Wrong email or password.",
        "auth/user-not-found": "No account with that email.",
        "auth/invalid-email": "That is not a valid email address.",
        "auth/too-many-requests": "Too many attempts. Wait a moment and try again.",
        "auth/operation-not-allowed":
          "Email/password sign-in is disabled on this Firebase project. Enable the Email/Password provider in Firebase Auth.",
      }
      setError(map[code] ?? (e instanceof Error ? e.message : String(e)))
    }
  }, [])

  const signOut = React.useCallback(async () => {
    await fbSignOut(getFirebaseAuth())
  }, [])

  const getToken = React.useCallback(async () => {
    const u = getFirebaseAuth().currentUser
    return u ? await u.getIdToken() : null
  }, [])

  const value = React.useMemo(
    () => ({
      user,
      loading,
      rejected,
      error,
      signIn,
      signInWithPassword: signInWithPasswordFn,
      signOut,
      getToken,
    }),
    [user, loading, rejected, error, signIn, signInWithPasswordFn, signOut, getToken],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const ctx = React.useContext(Ctx)
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>")
  return ctx
}

export { ALLOWED_DOMAINS, isAllowedEmail }
